import React, { useState, useEffect, useContext, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Button, Box, Typography, IconButton, Collapse, Menu, Tooltip,
  FormControlLabel, Switch, InputBase,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import HomeWorkIcon from '@material-ui/icons/HomeWork';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles, ThemeProvider } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import StarIcon from '@material-ui/icons/Star';
import AddIcon from '@material-ui/icons/Add';
import { toast } from 'react-toastify';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import PrintIcon from '@material-ui/icons/Print';
import { jsPDF } from 'jspdf';
import { useDispatch, useSelector } from 'react-redux';
import { createOrder, updateOrder } from '../../store/slices/transportOrdersSlice';
import { selectLocations, fetchLocations, createLocation } from '../../store/slices/locationsSlice';
import SkillsPicker from '../common/SkillsPicker';
import AOPetrotecOrderSection from './AOPetrotecOrderSection';
import FuelOrderSection from './FuelOrderSection';
import { recomputeRouteAfterOTChange, reoptimizeRouteAfterOTChange } from '../../utils/routeSync';
import { AuthContext } from '../../App';
import { t } from '../../i18n';
import { geocodeSearchMulti } from '../../utils/mapboxApi';
import { usePlannerTheme, usePlannerDialogTheme } from '../../theme/plannerTheme';

const useStyles = makeStyles(() => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 16,
      // Wider than before — the Recolha/Entrega sections now sit side-by-side
      // to reinforce the left-to-right journey metaphor for operators.
      maxWidth: 980,
      width: '100%',
      overflow: 'hidden',
    },
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    height: 56,
    background: ({ dark }) => dark ? '#122133' : '#fff',
    borderBottom: ({ dark }) => dark ? '1px solid rgba(110,193,228,0.16)' : '1px solid #e2e8f0',
  },
  content: {
    padding: '16px 24px 24px',
    overflowY: 'auto',
    maxHeight: 'calc(85vh - 130px)',
    background: ({ dark }) => dark ? '#0e1b2b' : '#fafbfc',
  },
  section: {
    marginBottom: 8,
    background: ({ dark }) => dark ? '#122133' : '#fff',
    borderRadius: 10,
    border: ({ dark }) => dark ? '1px solid rgba(110,193,228,0.16)' : '1px solid #e2e8f0',
    overflow: 'hidden',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    padding: '10px 14px',
    transition: 'background 0.12s',
    '&:hover': { background: ({ dark }) => dark ? 'rgba(255,255,255,0.04)' : '#f8fafc' },
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '0.78rem',
    color: ({ dark }) => dark ? '#eaf3fb' : '#1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  sectionBody: {
    padding: '0 14px 14px',
  },
  row: {
    display: 'flex',
    gap: 10,
    marginBottom: 8,
  },
  field: {
    '& .MuiOutlinedInput-root': { borderRadius: 8, background: ({ dark }) => dark ? 'rgba(255,255,255,0.04)' : '#fafbfc', fontSize: '0.78rem' },
    '& .MuiOutlinedInput-input': { padding: '10px 12px' },
    '& .MuiInputLabel-root': { fontSize: '0.74rem' },
  },
  actions: {
    padding: '12px 24px',
    borderTop: ({ dark }) => dark ? '1px solid rgba(110,193,228,0.16)' : '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    background: ({ dark }) => dark ? '#122133' : '#fff',
  },
  // Ghost primary — white with thin dark border. Matches the "Nova Ordem de
  // Serviço" button style across the app. No escuro: borda clara translúcida.
  saveBtn: {
    background: ({ dark }) => dark ? 'rgba(255,255,255,0.06)' : '#fff',
    color: ({ dark }) => dark ? '#eaf3fb' : '#0f172a',
    fontWeight: 500,
    fontSize: '0.78rem',
    letterSpacing: '-0.005em',
    textTransform: 'none',
    borderRadius: 8,
    padding: '7px 20px',
    boxShadow: 'none',
    border: ({ dark }) => dark ? '1px solid rgba(234,243,251,0.4)' : '1px solid #0f172a',
    transition: 'background 0.15s',
    '&:hover': { background: ({ dark }) => dark ? 'rgba(255,255,255,0.1)' : '#f8fafc', boxShadow: 'none' },
    '&.Mui-disabled': {
      background: ({ dark }) => dark ? 'rgba(255,255,255,0.04)' : '#f1f5f9',
      color: ({ dark }) => dark ? '#5b7384' : '#94a3b8',
      border: ({ dark }) => dark ? '1px solid rgba(110,193,228,0.16)' : '1px solid #e2e8f0',
    },
  },
  cancelBtn: {
    fontWeight: 500,
    fontSize: '0.78rem',
    textTransform: 'none',
    borderRadius: 8,
    color: ({ dark }) => dark ? '#9fb3c8' : '#64748b',
    padding: '7px 14px',
  },
  suggestions: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 1300,
    background: ({ dark }) => dark ? '#122133' : '#fff',
    border: ({ dark }) => dark ? '1px solid rgba(110,193,228,0.2)' : '1px solid rgba(0,0,0,0.1)',
    borderRadius: 8,
    maxHeight: 160,
    overflowY: 'auto',
    boxShadow: ({ dark }) => dark ? '0 4px 16px rgba(0,0,0,0.5)' : '0 4px 16px rgba(0,0,0,0.1)',
  },
  suggestionItem: {
    padding: '6px 12px',
    fontSize: '0.78rem',
    cursor: 'pointer',
    '&:hover': { background: ({ dark }) => dark ? 'rgba(255,255,255,0.06)' : '#f1f5f9' },
  },
}));

const emptyForm = {
  pickup: { locationName: '', address: '', contact: '', phone: '', windowStart: '', windowEnd: '', instructions: '', coordinates: null },
  delivery: { locationName: '', address: '', contact: '', phone: '', email: '', windowStart: '', windowEnd: '', instructions: '', coordinates: null },
  cargo: { description: '', weight: '', volume: '', pallets: '', type: 'Paletes' },
  agreedPrice: '',
  externalRef: '',
  customRef: '',
  // Obra ligada (Casais): herdada do favorito de ENTREGA quando este aponta
  // para uma obra. null = OT sem obra.
  obraId: null,
  priority: 'normal',
  // Contractual SLA in hours (Petrotec workflow). Empty = no SLA override.
  slaHours: '',
  slaResolutionHours: '',
  // Order kind: "delivery" (cargo transport) or "assistance" (technical service visit).
  serviceType: 'delivery',
  // Whether the route ends with a return leg back to the driver's home address.
  // Auto-checked by default for assistance OTs (the technician comes home at
  // the end of the day) and unchecked for delivery/fuel.
  returnHome: false,
  // Service duration in minutes (Petrotec: predicted on-site service time).
  unloadingTime: '',
  // Required driver skills for this order (array of skill keys).
  requiredSkills: [],
  // Explicit service date (YYYY-MM-DD) — for assistance orders, distinct from window.
  serviceDate: '',
  notes: '',
  driverId: '',
  vehicleId: '',
  // Petrotec Angola Fase A — secção opcional, só preenchida em modo AO.
  // Para tenants PT, fica como objecto vazio e nunca chega ao backend
  // porque a secção AO nem sequer aparece no DOM.
  aoPetrotec: {
    finalCustomerId: '',
    siteId: '',
    interventionType: '',
    equipmentIds: [],
    // 'covered' | 'external' | '' — controla se Sáb/Dom/feriados são
    // debitados no cálculo H.N./H.E. Vive no AssistanceReport mas
    // expomos no formulário da OT para o operador classificar logo.
    causeType: '',
    // 'avenca' | 'extra_avenca' | 'com_contrato' | 'sem_contrato' | '' —
    // tipo contratual da OT. Campo `contractType` em transport_orders.
    contractType: '',
    // Mapa { equipmentId: 'motivo' } preenchido pelo operador. Backend
    // grava como problemDescription em OrderEquipmentIntervention. A app
    // do motorista pode editar/sobrepor no terreno.
    equipmentProblems: {},
  },
  // ─── Move Express — Distribuição de combustível (opt-in) ──────────
  // Preenchido só quando serviceType === 'fuel'. Para os outros tipos
  // fica todo vazio e o backend ignora os campos.
  fuel: {
    fuelProduct: '',          // 'gasoleo' | 'gasolina' | 'jet_a1' | 'outro' | ''
    cisternCapacityM3: '',    // capacidade esperada (m³)
    expectedLitres: '',       // opcional — auto = capacidade × 1000
    isConvoy: false,          // operador marca se OT faz parte de comboio
    linkedToOtIds: [],        // ids das OTs ligadas
    coordinatedDelivery: false, // true = OT partilhada · false = vínculo visual
  },
};

// Four common SLA presets. Operator can also pick "Personalizado" and type any value.
const SLA_PRESETS = [
  { value: 2,  labelPt: 'Muito urgente (2h)', labelEn: 'Very urgent (2h)', color: '#dc2626' },
  { value: 6,  labelPt: 'Urgente (6h)',       labelEn: 'Urgent (6h)',      color: '#f97316' },
  { value: 24, labelPt: 'Não urgente (24h)',  labelEn: 'Non-urgent (24h)', color: '#eab308' },
  { value: 48, labelPt: 'Normal (48h)',       labelEn: 'Normal (48h)',     color: '#22c55e' },
];

// Convert ISO UTC datetime to local datetime-local input value (YYYY-MM-DDTHH:MM)
function isoToLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.slice(0, 16);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Convert datetime-local input ("YYYY-MM-DDTHH:MM" interpreted as local
// time in the browser) to a full ISO string with the operator's offset
// applied. Without this we'd send a naked "2026-05-07T14:00" that the
// backend (running in UTC) reads as UTC, shifting every saved window
// by +1h once the operator is in Europe/Lisbon. Returns null for empty
// input so the backend can clear the column.
function localToIso(localStr) {
  if (!localStr) return null;
  const d = new Date(localStr); // browser parses as local time
  return isNaN(d.getTime()) ? null : d.toISOString();
}

function orderToForm(order) {
  if (!order) return { ...emptyForm };
  // Backend returns flat fields (pickupName, pickupAddress, etc.)
  const priorityMap = { 1: 'high', 0: 'normal', '-1': 'low' };
  return {
    pickup: {
      locationName: order.pickupName || order.pickup?.locationName || '',
      address: order.pickupAddress || order.pickup?.address || '',
      contact: order.pickupContact || order.pickup?.contact || '',
      phone: order.pickupPhone || order.pickup?.phone || '',
      windowStart: isoToLocal(order.pickupWindowFrom),
      windowEnd: isoToLocal(order.pickupWindowTo),
      instructions: order.pickupInstructions || order.pickup?.instructions || '',
      coordinates: (order.pickupLng && order.pickupLat) ? [Number(order.pickupLng), Number(order.pickupLat)] : null,
    },
    delivery: {
      locationName: order.deliveryName || order.delivery?.locationName || '',
      address: order.deliveryAddress || order.delivery?.address || '',
      contact: order.deliveryContact || order.delivery?.contact || '',
      phone: order.deliveryPhone || order.delivery?.phone || '',
      email: order.deliveryEmail || order.delivery?.email || '',
      windowStart: isoToLocal(order.deliveryWindowFrom),
      windowEnd: isoToLocal(order.deliveryWindowTo),
      instructions: order.deliveryInstructions || order.delivery?.instructions || '',
      coordinates: (order.deliveryLng && order.deliveryLat) ? [Number(order.deliveryLng), Number(order.deliveryLat)] : null,
    },
    cargo: {
      description: order.cargoDescription || order.cargo?.description || '',
      weight: order.cargoWeight || order.cargo?.weight || '',
      volume: order.cargoVolume || order.cargo?.volume || '',
      pallets: order.cargoPallets || order.cargo?.pallets || '',
      type: order.cargoType || order.cargo?.type || 'Paletes',
    },
    agreedPrice: order.agreedPrice || '',
    externalRef: order.externalRef || '',
    customRef: order.customRef || '',
    priority: priorityMap[order.priority] || (typeof order.priority === 'string' ? order.priority : 'normal'),
    slaHours: order.slaHours != null ? String(order.slaHours) : '',
    slaResolutionHours: order.slaResolutionHours != null ? String(order.slaResolutionHours) : '',
    serviceType: ['assistance', 'fuel'].includes(order.serviceType) ? order.serviceType : 'delivery',
    obraId: order.obraId != null ? order.obraId : null,
    // returnHome: explicit value if the OT has been saved before, otherwise
    // default to true for assistance (technician comes home) and false for
    // delivery/fuel. The operator can toggle this in the form.
    returnHome: typeof order.returnHome === 'boolean'
      ? order.returnHome
      : (order.serviceType === 'assistance'),
    unloadingTime: order.unloadingTime != null ? String(order.unloadingTime) : '',
    requiredSkills: Array.isArray(order.requiredSkills) ? order.requiredSkills : [],
    serviceDate: order.serviceDate ? String(order.serviceDate).slice(0, 10) : '',
    notes: order.notes || '',
    driverId: order.driverId || '',
    vehicleId: order.vehicleId || '',
    aoPetrotec: {
      finalCustomerId: order.finalCustomerId || '',
      siteId: order.siteId || '',
      interventionType: order.interventionType || '',
      // Em edição, os equipmentIds vêm de uma chamada separada (não estão
      // no payload base da OT). Por agora não pré-preenchemos no editar —
      // a edição dos equipamentos far-se-á no detalhe da OT (passo 8).
      equipmentIds: [],
      causeType: order.assistanceReport?.causeType || '',
      contractType: order.contractType || '',
      equipmentProblems: {},
    },
    fuel: {
      fuelProduct: order.fuelProduct || '',
      cisternCapacityM3: order.cisternCapacityM3 != null ? String(order.cisternCapacityM3) : '',
      expectedLitres: order.loadingOrderQty != null ? String(order.loadingOrderQty) : '',
      // Em edit, considera-se comboio se há OTs ligadas OU a flag está ligada
      isConvoy: !!(order.coordinatedDelivery || (Array.isArray(order.linkedToOtIds) && order.linkedToOtIds.length > 0)),
      linkedToOtIds: Array.isArray(order.linkedToOtIds) ? order.linkedToOtIds : [],
      coordinatedDelivery: !!order.coordinatedDelivery,
    },
  };
}

function AddressField({ label, value, onChange, onSelect, classes, lang, country }) {
  const [suggestions, setSuggestions] = useState([]);
  const [focused, setFocused] = useState(false);
  const debounceRef = useRef(null);

  function handleChange(e) {
    const val = e.target.value;
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (val.length >= 3) {
        try {
          const results = await geocodeSearchMulti(val, { country: country || 'PT' });
          setSuggestions(results || []);
        } catch { setSuggestions([]); }
      } else {
        setSuggestions([]);
      }
    }, 350);
  }

  function handleSelect(item) {
    onChange(item.name);
    onSelect(item);
    setSuggestions([]);
  }

  return (
    <Box style={{ position: 'relative', flex: 1 }}>
      <TextField
        className={classes.field}
        fullWidth
        variant="outlined"
        size="small"
        label={label}
        value={value}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 200)}
      />
      {focused && suggestions.length > 0 && (
        <Box className={classes.suggestions}>
          {suggestions.map((s, i) => {
            // Tag cada sugestão com a fonte. Google Places traz
            // POIs (pastelarias, restaurantes, lojas), Mapbox e HERE
            // são bons para moradas, OSM preenche lacunas. Operador
            // percebe imediatamente quando um resultado é um POI rico.
            const srcCfg = ({
              'google-geo': { label: 'Morada', color: '#15803d', bg: 'rgba(22,163,74,0.12)' },
              google: { label: 'Places', color: '#1a73e8', bg: 'rgba(26,115,232,0.10)' },
              mapbox: { label: 'Mapbox', color: '#475569', bg: 'rgba(71,85,105,0.10)' },
              here:   { label: 'HERE',   color: '#0ea5e9', bg: 'rgba(14,165,233,0.10)' },
              osm:    { label: 'OSM',    color: '#16a34a', bg: 'rgba(22,163,74,0.10)' },
            })[s.source] || null;
            return (
              <Box
                key={i}
                className={classes.suggestionItem}
                onMouseDown={() => handleSelect(s)}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.name}
                </span>
                {srcCfg && (
                  <span style={{
                    fontSize: '0.52rem', fontWeight: 800,
                    padding: '1px 6px', borderRadius: 999,
                    color: srcCfg.color, background: srcCfg.bg,
                    letterSpacing: '0.04em', textTransform: 'uppercase',
                    flexShrink: 0,
                  }}>
                    {srcCfg.label}
                  </span>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

function CollapsibleSection({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const dark = usePlannerTheme() === 'dark';
  const classes = useStyles({ dark });
  return (
    <Box className={classes.section}>
      <Box className={classes.sectionHeader} onClick={() => setOpen(!open)}>
        <Typography className={classes.sectionTitle}>{title}</Typography>
        {open ? <ExpandLessIcon style={{ fontSize: 18, color: '#94a3b8' }} /> : <ExpandMoreIcon style={{ fontSize: 18, color: '#94a3b8' }} />}
      </Box>
      <Collapse in={open}>{children}</Collapse>
    </Box>
  );
}

function OrderForm({ open, onClose, order, lang, viewOnly = false }) {
  const dark = usePlannerTheme() === 'dark';
  const classes = useStyles({ dark });
  const dialogTheme = usePlannerDialogTheme();
  const dispatch = useDispatch();
  const { token, clientSettings } = useContext(AuthContext);
  // Variante mercadoria (Casais): mostra o "Custo da entrega" em destaque.
  const isMercadoria = clientSettings?.uiVariant === 'mercadoria';
  // Documentos escolhidos na criação/edição — anexados à OT ao gravar (depois
  // de a OT ter id). Para novas OT o upload só pode ser após o POST de criação.
  const [pendingDocs, setPendingDocs] = useState([]);
  // Client profile restricts which order types this operator can create.
  // We previously used `['delivery','assistance']` as a fallback when the
  // client profile hadn't loaded yet, but that caused fuel-only clients to
  // silently save OTs as `delivery` if the form opened fast enough. Now we
  // gate the form on a loaded profile and use the profile's list verbatim.
  const allowedOrderTypesRaw = clientSettings?.allowedOrderTypes;
  const allowedTypesLoaded = Array.isArray(allowedOrderTypesRaw);
  const allowedTypes = allowedTypesLoaded && allowedOrderTypesRaw.length > 0
    ? allowedOrderTypesRaw
    : ['delivery', 'assistance'];
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const locations = useSelector(selectLocations);
  const [favoriteAnchor, setFavoriteAnchor] = useState(null); // { el, sectionKey }
  const [favSearch, setFavSearch] = useState(''); // pesquisa no seletor de favoritos
  const [saveFavDialog, setSaveFavDialog] = useState(null); // { sectionKey, name, type, address, notes, isPreferred, saving }
  // Fase 1 — guarda morada↔rota: quando se altera o local de serviço de
  // uma OT que já pertence a uma rota, avisa o operador conforme o estado
  // da rota antes de gravar. { scenario, routeName, driverName, data, orderId }
  const [routeGuard, setRouteGuard] = useState(null);

  // O OrderForm fica sempre montado (open controla o Dialog). Se a janela
  // da OT fechar, descarta uma guarda pendente — senão o diálogo de
  // confirmação ficava órfão e gravava já sem a OT em contexto.
  useEffect(() => {
    if (!open) setRouteGuard(null);
  }, [open]);

  const isEdit = !!order;

  // If the configured allowedTypes doesn't include the current serviceType,
  // snap it to the first allowed value so the form is never in an invalid state.
  // Only runs once the client profile has actually loaded — otherwise the
  // fallback (`delivery`) would overwrite the operator's choice.
  useEffect(() => {
    if (!allowedTypesLoaded) return;
    if (!allowedTypes.includes(form.serviceType)) {
      setForm(f => ({ ...f, serviceType: allowedTypes[0] || 'delivery' }));
    }
  }, [allowedTypesLoaded, allowedTypes.join(','), form.serviceType]); // eslint-disable-line react-hooks/exhaustive-deps

  // returnHome is no longer settable per-OT — the operator picks the route's
  // arrival point in the assign-vehicle dialog (driver home / favourite /
  // service location). The legacy auto-mirror on serviceType change has been
  // removed; the field stays at whatever the OT was created with (default
  // false) and the dispatch dialog is the source of truth.

  // (Previous auto-fill of pickup with the client's "Local de partida" was
  // removed — for fuel OTs the pickup is the supplier depot, not the truck
  // yard. The yard is injected as the *route* origin at the handleCreateRouteFromOT
  // step in OrdersView.)

  useEffect(() => {
    if (open) {
      setForm(orderToForm(order));
      // Fetch drivers, vehicles, and favorite locations
      if (token) {
        fetch('/planner-api/drivers', { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.json()).then(d => setDrivers(Array.isArray(d) ? d : [])).catch(() => {});
        fetch('/planner-api/vehicles', { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.json()).then(d => setVehicles(Array.isArray(d) ? d : [])).catch(() => {});
        dispatch(fetchLocations(token));
      }
    }
  }, [open, order, token, dispatch]);

  // Open the "save as favorite" mini-dialog pre-filled with the current section's data.
  // We open the dialog UNCONDITIONALLY and resolve coords + validation inside it — keeps the
  // operator in control and avoids silent early-returns.
  function openSaveFavoriteDialog(sectionKey) {
    const section = form[sectionKey] || {};
    const coords = section.coordinates;
    const name = (section.locationName || '').trim();
    const address = (section.address || '').trim();
    const defaultType = sectionKey === 'pickup' ? 'warehouse' : 'poi';
    setSaveFavDialog({
      sectionKey,
      name: name || (address ? address.split(',')[0] : ''),
      type: defaultType,
      address,
      coordinates: (coords && coords.length === 2) ? coords : null,
      notes: section.instructions || '',
      isPreferred: true,
      saving: false,
      geocoding: false,
    });
  }

  // Live-geocode the address from within the mini-dialog (triggered by user clicking "Localizar")
  async function geocodeSaveFavAddress() {
    if (!saveFavDialog?.address) return;
    setSaveFavDialog(s => ({ ...s, geocoding: true }));
    try {
      const results = await geocodeSearchMulti(saveFavDialog.address, { country: clientSettings?.defaultCountry || 'PT' });
      if (results && results.length > 0 && results[0].coordinates) {
        setSaveFavDialog(s => ({ ...s, coordinates: results[0].coordinates, geocoding: false }));
      } else {
        setSaveFavDialog(s => ({ ...s, geocoding: false }));
        toast.error(lang === 'pt' ? 'Morada não encontrada' : 'Address not found');
      }
    } catch (err) {
      setSaveFavDialog(s => ({ ...s, geocoding: false }));
      toast.error(lang === 'pt' ? 'Erro ao localizar morada' : 'Geocoding failed');
    }
  }

  async function commitSaveFavorite() {
    if (!saveFavDialog) return;
    const { sectionKey, name, type, address, coordinates, notes, isPreferred } = saveFavDialog;
    if (!name.trim()) {
      toast.error(lang === 'pt' ? 'Dá um nome ao favorito' : 'Name is required');
      return;
    }
    if (!coordinates || coordinates.length !== 2) {
      toast.error(lang === 'pt'
        ? 'Carrega em "Localizar" para encontrar as coordenadas da morada'
        : 'Click "Locate" to resolve coordinates from the address');
      return;
    }
    setSaveFavDialog(s => ({ ...s, saving: true }));
    try {
      const created = await dispatch(createLocation({
        token,
        location: {
          name: name.trim(),
          type,
          latitude: Number(coordinates[1]),
          longitude: Number(coordinates[0]),
          address,
          notes: notes || null,
          isPreferred: !!isPreferred,
        },
      })).unwrap();
      toast.success(lang === 'pt' ? `✓ "${created.name}" guardado nos favoritos` : `✓ "${created.name}" saved to favorites`);
      // Reflect the freshly-saved favorite back on the OT section (name + address + coords stay)
      setForm(prev => ({
        ...prev,
        [sectionKey]: {
          ...prev[sectionKey],
          locationName: created.name,
        },
      }));
      setSaveFavDialog(null);
    } catch (err) {
      setSaveFavDialog(s => ({ ...s, saving: false }));
      toast.error(lang === 'pt' ? `Falha ao guardar: ${err.message || err}` : `Save failed: ${err.message || err}`);
    }
  }

  function pickFavorite(sectionKey, loc) {
    // Fill the section with the selected favorite location's data.
    // ClientLocation has: name, address, latitude, longitude, notes — no contact/phone.
    setForm(prev => ({
      ...prev,
      // Obra: a ENTREGA ligada a um favorito-obra faz a OT herdar essa obra
      // (→ selo na lista, dashboard/email ao chefe de obra). Escolher na
      // entrega um favorito SEM obra limpa a ligação. A recolha não mexe.
      ...(sectionKey === 'delivery' ? { obraId: loc.obraId || null } : {}),
      [sectionKey]: {
        ...prev[sectionKey],
        locationName: loc.name || '',
        address: loc.address || '',
        coordinates: (loc.latitude != null && loc.longitude != null)
          ? [Number(loc.longitude), Number(loc.latitude)]
          : prev[sectionKey].coordinates,
        instructions: loc.notes || prev[sectionKey].instructions,
      },
    }));
    setFavoriteAnchor(null);
    setFavSearch('');
  }

  function updateField(section, field, value) {
    if (section) {
      setForm(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
    } else {
      setForm(prev => ({ ...prev, [field]: value }));
    }
  }

  async function handleSubmit() {
    // Guard: don't let the operator save before the client profile has loaded,
    // otherwise the form might default to `delivery` when the client only
    // allows `fuel` (or similar mismatches).
    if (!allowedTypesLoaded) {
      setError(lang === 'pt' ? 'A carregar preferências do cliente… tenta novamente.' : 'Loading client preferences… retry.');
      return;
    }
    // Sanity check: the chosen serviceType must be in the allowed list.
    if (!allowedTypes.includes(form.serviceType)) {
      setError(lang === 'pt'
        ? `Tipo "${form.serviceType}" não permitido para este cliente.`
        : `Type "${form.serviceType}" not allowed for this client.`);
      return;
    }
    // Validate time windows: "To" must be >= "From"
    if (form.pickup.windowStart && form.pickup.windowEnd && form.pickup.windowEnd < form.pickup.windowStart) {
      setError(lang === 'pt' ? 'Recolha: a data "Até" não pode ser anterior à data "De"' : 'Pickup: "To" date cannot be before "From" date');
      return;
    }
    if (form.delivery.windowStart && form.delivery.windowEnd && form.delivery.windowEnd < form.delivery.windowStart) {
      setError(lang === 'pt' ? 'Entrega: a data "Até" não pode ser anterior à data "De"' : 'Delivery: "To" date cannot be before "From" date');
      return;
    }
    // Delivery window should not be before pickup window
    if (form.pickup.windowStart && form.delivery.windowStart && form.delivery.windowStart < form.pickup.windowStart) {
      setError(lang === 'pt' ? 'A janela de entrega não pode ser anterior à janela de recolha' : 'Delivery window cannot be before pickup window');
      return;
    }

    setSaving(true);
    try {
      // Best-effort geocode any address where the operator typed without picking a suggestion.
      // Prevents "name filled but no coords" — which silently breaks route calculation later.
      async function ensureCoords(section) {
        if (section.coordinates && section.coordinates.length === 2) return section.coordinates;
        const addr = (section.address || section.locationName || '').trim();
        if (addr.length < 3) return null;
        try {
          const results = await geocodeSearchMulti(addr, { country: clientSettings?.defaultCountry || 'PT' });
          if (results && results[0]?.coordinates) return results[0].coordinates;
        } catch { /* ignore */ }
        return null;
      }
      const pickupCoords = await ensureCoords(form.pickup);
      const deliveryCoords = await ensureCoords(form.delivery);

      // Assistance OTs only have a service location (= delivery). The
      // pickup fields stay empty on save — consumers that read pickup
      // (consolidator, route building, list display) already fall back
      // to delivery for assistance via dedicated branches. Mirroring the
      // delivery onto pickup as a "compat shim" was duplicating data and
      // confusing operators in the list view.
      const isAssistance = form.serviceType === 'assistance';
      const pickupSrc = isAssistance ? null : form.pickup;
      const pickupCoordsFinal = isAssistance ? null : pickupCoords;

      const data = {
        pickupName: pickupSrc?.locationName || undefined,
        pickupAddress: pickupSrc?.address || undefined,
        pickupLat: pickupCoordsFinal?.[1] || undefined,
        pickupLng: pickupCoordsFinal?.[0] || undefined,
        pickupContact: pickupSrc?.contact || undefined,
        pickupPhone: pickupSrc?.phone || undefined,
        // Use null (not undefined) so the backend explicitly clears the field.
        // `undefined` is dropped by JSON.stringify and the PATCH handler then
        // skips the column entirely — meaning a user who clears the date in
        // the picker would see it stay populated in the DB.
        pickupWindowFrom: localToIso(pickupSrc?.windowStart),
        pickupWindowTo: localToIso(pickupSrc?.windowEnd),
        pickupInstructions: pickupSrc?.instructions || undefined,
        deliveryName: form.delivery.locationName || undefined,
        deliveryAddress: form.delivery.address || undefined,
        deliveryLat: deliveryCoords?.[1] || undefined,
        deliveryLng: deliveryCoords?.[0] || undefined,
        deliveryContact: form.delivery.contact || undefined,
        deliveryPhone: form.delivery.phone || undefined,
        deliveryEmail: form.delivery.email || undefined,
        deliveryWindowFrom: localToIso(form.delivery.windowStart),
        deliveryWindowTo: localToIso(form.delivery.windowEnd),
        deliveryInstructions: form.delivery.instructions || undefined,
        // Obra (variante mercadoria Casais): herdada quando a ENTREGA é
        // preenchida a partir de um favorito ligado a uma obra. null = sem obra.
        obraId: form.obraId != null ? form.obraId : null,
        cargoDescription: form.cargo.description || undefined,
        cargoWeight: form.cargo.weight ? Number(form.cargo.weight) : undefined,
        cargoVolume: form.cargo.volume ? Number(form.cargo.volume) : undefined,
        cargoPallets: form.cargo.pallets ? Number(form.cargo.pallets) : undefined,
        cargoType: form.cargo.type || undefined,
        agreedPrice: form.agreedPrice ? Number(form.agreedPrice) : undefined,
        externalRef: form.externalRef || undefined,
        customRef: form.customRef || undefined,
        priority: form.priority === 'high' ? 1 : form.priority === 'low' ? -1 : 0,
        slaHours: form.slaHours !== '' && form.slaHours != null ? Number(form.slaHours) : null,
        slaResolutionHours: form.slaResolutionHours !== '' && form.slaResolutionHours != null ? Number(form.slaResolutionHours) : null,
        serviceType: form.serviceType || 'delivery',
        // returnHome is no longer set from OrderForm — operator chooses arrival
        // in the assign-vehicle dialog. Keep field at false so old DB rows with
        // returnHome=true don't accidentally survive an edit-and-save.
        returnHome: false,
        unloadingTime: form.unloadingTime !== '' && form.unloadingTime != null ? Number(form.unloadingTime) : null,
        requiredSkills: Array.isArray(form.requiredSkills) ? form.requiredSkills : [],
        serviceDate: form.serviceDate || null,
        notes: form.notes || undefined,
        // driverId/vehicleId removed — assignment done via Dispatch
        // Petrotec Angola — só enviar quando preenchido. Em modo PT (toggle
        // a 'pt'), o componente AOPetrotecOrderSection não renderiza e estes
        // campos ficam vazios → enviam-se undefined e o backend não altera nada.
        finalCustomerId: form.aoPetrotec?.finalCustomerId || undefined,
        siteId: form.aoPetrotec?.siteId || undefined,
        interventionType: form.aoPetrotec?.interventionType || undefined,
        equipmentIds: Array.isArray(form.aoPetrotec?.equipmentIds) && form.aoPetrotec.equipmentIds.length > 0
          ? form.aoPetrotec.equipmentIds
          : undefined,
        // causeType deixou de ser enviado pelo form — o backend deriva
        // do contractType (Avença/Com contrato → covered; Extra avença/
        // Sem contrato → external). Acordo com o operador Petrotec.
        contractType: form.aoPetrotec?.contractType || undefined,
        // Mapa { eqId: 'motivo' } — só envia quando tem chaves. Backend
        // grava no problemDescription da OrderEquipmentIntervention.
        equipmentProblems: (form.aoPetrotec?.equipmentProblems
          && Object.keys(form.aoPetrotec.equipmentProblems).length > 0)
          ? form.aoPetrotec.equipmentProblems
          : undefined,
        // ── Move Express — Distribuição combustível ──────────────────
        // Só envia quando há serviceType=fuel + algum campo preenchido.
        // expectedLitres → loadingOrderQty (litros esperados, depois
        // confirmados na entrega).
        ...(form.serviceType === 'fuel' ? {
          fuelProduct: form.fuel?.fuelProduct || undefined,
          cisternCapacityM3: form.fuel?.cisternCapacityM3 !== '' && form.fuel?.cisternCapacityM3 != null
            ? Number(form.fuel.cisternCapacityM3) : undefined,
          loadingOrderQty: form.fuel?.expectedLitres !== '' && form.fuel?.expectedLitres != null
            ? Number(form.fuel.expectedLitres) : undefined,
          linkedToOtIds: form.fuel?.isConvoy && Array.isArray(form.fuel.linkedToOtIds) && form.fuel.linkedToOtIds.length > 0
            ? form.fuel.linkedToOtIds : undefined,
          coordinatedDelivery: form.fuel?.isConvoy ? !!form.fuel?.coordinatedDelivery : undefined,
        } : {}),
      };

      let savedOtId = null;
      if (isEdit) {
        // Fase 1 — guarda morada↔rota. Se o local de serviço mudou e a
        // OT pertence a uma rota, avalia o estado e avisa o operador
        // antes de gravar (ou bloqueia, se a OT já estiver concluída).
        const guard = await evaluateRouteGuard(data);
        if (guard?.blocked) { setError(guard.message); return; }
        if (guard) {
          setRouteGuard({ ...guard, data, orderId: order._id || order.id });
          return;
        }
        await dispatch(updateOrder({ token, id: order._id || order.id, data })).unwrap();
        savedOtId = order._id || order.id;
      } else {
        const saved = await dispatch(createOrder({ token, data })).unwrap();
        savedOtId = saved?.id || saved?._id || saved?.data?.id || null;
      }
      // Documentos pendentes → anexar à OT (já tem id). Best-effort.
      if (savedOtId && pendingDocs.length) {
        for (const f of pendingDocs) {
          try {
            const fd = new FormData();
            fd.append('file', f);
            fd.append('documentType', 'outro');
            await fetch(`/planner-api/transport-orders/${savedOtId}/documents`, {
              method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
            });
          } catch (e) { console.error('OT document upload error:', e); }
        }
      }
      onClose(true);
    } catch (err) {
      console.error('Order save error:', err);
      setError(saveErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  // Mensagem de erro legível a partir do payload estruturado que o slice
  // devolve em caso de falha (ex.: customRef duplicado).
  function saveErrorMessage(err) {
    const payload = err && typeof err === 'object' ? err : { error: String(err || '') };
    let msg = payload.error || (lang === 'pt'
      ? 'Erro ao gravar a OT. Verifica os campos e tenta de novo.'
      : 'Error saving the OT. Check the fields and try again.');
    if (payload.field === 'customRef') msg = payload.error || msg;
    return msg;
  }

  // Fase 1 — avalia o impacto na rota de alterar o local de serviço.
  // Devolve:
  //   null                        → sem impacto, gravar normalmente
  //   { blocked, message }         → bloquear (OT já concluída)
  //   { scenario, routeName, ... } → mostrar confirmação antes de gravar
  // (A Fase 2 passará a recalcular a rota; por agora apenas avisa.)
  async function evaluateRouteGuard(data) {
    const oLat = order.deliveryLat != null ? Number(order.deliveryLat) : null;
    const oLng = order.deliveryLng != null ? Number(order.deliveryLng) : null;
    const nLat = data.deliveryLat != null ? Number(data.deliveryLat) : null;
    const nLng = data.deliveryLng != null ? Number(data.deliveryLng) : null;
    const coordChanged =
      (oLat == null) !== (nLat == null) ||
      (oLat != null && nLat != null &&
        (Math.abs(oLat - nLat) > 1e-6 || Math.abs(oLng - nLng) > 1e-6));
    const addrChanged =
      (order.deliveryAddress || '').trim() !== (data.deliveryAddress || '').trim();
    const locationChanged = coordChanged || addrChanged;

    // Mudança de JANELA HORÁRIA — comparada por timestamp (robusto a formatos
    // diferentes entre o que vem da BD e o ISO gerado pelo formulário).
    const wt = (v) => { const t = v ? new Date(v).getTime() : null; return Number.isFinite(t) ? t : null; };
    const windowChanged =
      wt(order.deliveryWindowFrom) !== wt(data.deliveryWindowFrom) ||
      wt(order.deliveryWindowTo)   !== wt(data.deliveryWindowTo) ||
      wt(order.pickupWindowFrom)   !== wt(data.pickupWindowFrom) ||
      wt(order.pickupWindowTo)     !== wt(data.pickupWindowTo);

    if (!locationChanged && !windowChanged) return null;
    const changeKind = locationChanged ? (windowChanged ? 'both' : 'location') : 'window';

    // OT já concluída — o local de serviço é história, não se altera. (Só
    // bloqueia se mexeram no LOCAL; uma janela de uma OT já entregue é inócua.)
    if (order.status === 'delivered' && locationChanged) {
      return {
        blocked: true,
        message: lang === 'pt'
          ? 'Esta OT já foi concluída (entregue) — o local de serviço não pode ser alterado.'
          : 'This OT is already delivered — its service location cannot be changed.',
      };
    }

    const routeId = order.assignedRouteId;
    if (!routeId) return null; // sem rota — gravar normalmente

    // Estado actual da rota.
    let route = null;
    try {
      const res = await fetch(`/planner-api/routes/${routeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) route = await res.json();
    } catch { /* rede indisponível — cai no aviso genérico abaixo */ }

    const status = route?.status || null;
    // Rota terminada/cancelada — alterar a OT não afecta nada de activo.
    if (status === 'completed' || status === 'cancelled') return null;

    const routeName = route?.name || `Rota #${routeId}`;
    const driverName = route?.driver?.name || order.driverName || null;

    if (status === 'in_progress') {
      return { scenario: 'in_transit', routeId, routeName, driverName, changeKind };
    }
    // draft / planned (ou estado desconhecido por falha de rede)
    return { scenario: driverName ? 'assigned' : 'planned', routeId, routeName, driverName, changeKind };
  }

  // Grava a OT depois de o operador confirmar o aviso da guarda morada↔rota.
  // Usa o id capturado na guarda (a prop `order` pode já não estar disponível).
  async function commitGuardedSave() {
    const data = routeGuard?.data;
    const orderId = routeGuard?.orderId;
    setRouteGuard(null);
    if (!data || !orderId) return;
    setSaving(true);
    setError('');
    try {
      await dispatch(updateOrder({ token, id: orderId, data })).unwrap();
      onClose(true);
    } catch (err) {
      console.error('Order save error:', err);
      setError(saveErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  // Fase 2 — grava a OT E recalcula a geometria da rota com a nova
  // morada, mantendo a ordem das paragens. Se a OT gravar mas o recálculo
  // falhar, a OT não se perde — avisa-se e fecha-se à mesma.
  async function commitAndSyncRoute() {
    const g = routeGuard;
    setRouteGuard(null);
    if (!g?.data || !g.orderId) return;
    setSaving(true);
    setError('');
    try {
      await dispatch(updateOrder({ token, id: g.orderId, data: g.data })).unwrap();
      const sync = await recomputeRouteAfterOTChange({
        routeId: g.routeId,
        otId: g.orderId,
        lat: g.data.deliveryLat,
        lng: g.data.deliveryLng,
        name: g.data.deliveryName || g.data.deliveryAddress,
        token,
      });
      if (sync.ok) {
        toast.success(lang === 'pt'
          ? 'OT gravada e rota recalculada com a nova morada.'
          : 'OT saved and route recomputed with the new address.');
      } else {
        // A OT ficou gravada — só o recálculo falhou.
        toast.warn(lang === 'pt'
          ? `OT gravada, mas a rota não foi recalculada: ${sync.error || 'erro'}. Recalcula-a no planeamento.`
          : `OT saved, but the route was not recomputed: ${sync.error || 'error'}.`);
      }
      onClose(true);
    } catch (err) {
      console.error('Order save error:', err);
      setError(saveErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  // Fase 3 — grava a OT E re-optimiza uma rota EM TRÂNSITO: reordena as
  // paragens por entregar a partir do GPS actual do motorista. A app do
  // motorista recebe `route:updated` e refaz a rota.
  async function commitAndReoptimize() {
    const g = routeGuard;
    setRouteGuard(null);
    if (!g?.data || !g.orderId) return;
    setSaving(true);
    setError('');
    try {
      await dispatch(updateOrder({ token, id: g.orderId, data: g.data })).unwrap();
      const r = await reoptimizeRouteAfterOTChange({
        routeId: g.routeId,
        otId: g.orderId,
        lat: g.data.deliveryLat,
        lng: g.data.deliveryLng,
        name: g.data.deliveryName || g.data.deliveryAddress,
        token,
      });
      if (r.ok) {
        toast.success(lang === 'pt'
          ? 'OT gravada e rota re-optimizada. O motorista foi notificado.'
          : 'OT saved and route re-optimized. The driver was notified.');
      } else {
        toast.warn(lang === 'pt'
          ? `OT gravada, mas a rota não foi re-optimizada: ${r.error || 'erro'}.`
          : `OT saved, but the route was not re-optimized: ${r.error || 'error'}.`);
      }
      onClose(true);
    } catch (err) {
      console.error('Order save error:', err);
      setError(saveErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function renderLocationSection(key, title) {
    const section = form[key];
    return (
      <CollapsibleSection title={title}>
        {/* Favorite location picker — fills name, address and coordinates from a saved location */}
        <Box style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '6px 10px', marginBottom: 8,
          background: '#fafbfc',
          border: '1px solid #e2e8f0', borderRadius: 8,
        }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <StarIcon style={{ fontSize: 14, color: '#94a3b8' }} />
            <Typography style={{ fontSize: '0.66rem', fontWeight: 700, color: '#475569' }}>
              {lang === 'pt' ? 'Preencher a partir de um favorito' : 'Fill from a favorite'}
            </Typography>
            <Typography style={{ fontSize: '0.56rem', color: '#94a3b8' }}>
              · {locations.length} {lang === 'pt' ? 'guardados' : 'saved'}
            </Typography>
          </Box>
          <Box style={{ display: 'flex', gap: 4 }}>
            <Tooltip title={lang === 'pt'
              ? ((section.address || section.locationName) ? 'Guardar local actual nos favoritos' : 'Preenche primeiro a morada ou nome')
              : ((section.address || section.locationName) ? 'Save current location as favorite' : 'Fill the address or name first')}>
              <span>
                <Button
                  size="small"
                  onClick={() => openSaveFavoriteDialog(key)}
                  disabled={!(section.address || section.locationName)}
                  startIcon={<AddIcon style={{ fontSize: 14 }} />}
                  style={{
                    fontSize: '0.66rem', fontWeight: 700, textTransform: 'none',
                    padding: '3px 10px', borderRadius: 6,
                    background: '#fff', color: '#475569',
                    border: '1px solid #e2e8f0', minWidth: 0,
                  }}
                >
                  {lang === 'pt' ? 'Guardar' : 'Save'}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={lang === 'pt'
              ? (locations.length ? 'Escolher um favorito guardado' : 'Sem favoritos — usa "Guardar" para criar o primeiro')
              : (locations.length ? 'Pick a saved favorite' : 'No favorites — use "Save" to create the first one')}>
              <span>
                <Button
                  size="small"
                  onClick={(e) => setFavoriteAnchor({ el: e.currentTarget, sectionKey: key })}
                  disabled={locations.length === 0}
                  endIcon={<ExpandMoreIcon style={{ fontSize: 14 }} />}
                  style={{
                    fontSize: '0.66rem', fontWeight: 700, textTransform: 'none',
                    padding: '3px 10px', borderRadius: 6,
                    background: '#fff', color: '#475569',
                    border: '1px solid #e2e8f0', minWidth: 0,
                  }}
                >
                  {lang === 'pt' ? 'Escolher' : 'Pick'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
        {/* Driver-home quick picker — only for pickup on assistance orders.
            Uses a Select (instead of chips) so it scales to any number of
            drivers without overflowing the layout. Material-UI v4 Select
            works reliably inside Dialogs (unlike Autocomplete+Popper). */}
        {key === 'pickup' && form.serviceType === 'assistance' && (() => {
          const driversWithHome = drivers.filter(d => d.homeAddress && d.homeLat && d.homeLng);
          if (driversWithHome.length === 0) return null;
          const selectedDriver = driversWithHome.find(d => section.coordinates
            && Math.abs(Number(section.coordinates[1]) - Number(d.homeLat)) < 0.0002
            && Math.abs(Number(section.coordinates[0]) - Number(d.homeLng)) < 0.0002);
          return (
            <Box style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 10px', marginBottom: 8,
              background: '#fafbfc',
              border: '1px solid #e2e8f0', borderRadius: 8,
            }}>
              <Typography style={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569', flexShrink: 0 }}>
                🏠 {lang === 'pt' ? 'Morada do motorista' : "Driver's home"}
              </Typography>
              {/* Native HTML <select> — 100% fiável dentro de Dialog, sem
                  problemas de portal/z-index que afectam o MUI Select.
                  Perde um pouco de estilo mas garante que funciona. */}
              <select
                value={selectedDriver?.id ?? ''}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  const d = driversWithHome.find(x => Number(x.id) === id);
                  if (!d) return;
                  updateField(key, 'locationName', d.homeAddress || `${d.name} (casa)`);
                  updateField(key, 'address', d.homeAddress);
                  updateField(key, 'coordinates', [Number(d.homeLng), Number(d.homeLat)]);
                }}
                style={{
                  flex: 1, minWidth: 240, height: 36,
                  fontSize: '0.78rem', padding: '0 10px',
                  border: '1px solid #cbd5e1', borderRadius: 6,
                  background: '#fff', color: '#0f172a',
                  cursor: 'pointer', outline: 'none',
                  fontFamily: 'inherit',
                }}
              >
                <option value="">
                  — {lang === 'pt' ? `Escolher motorista (${driversWithHome.length})` : `Pick driver (${driversWithHome.length})`} —
                </option>
                {driversWithHome.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.name} — {d.homeAddress}
                  </option>
                ))}
              </select>
            </Box>
          );
        })()}
        <Box className={classes.row}>
          <TextField
            className={classes.field}
            fullWidth
            variant="outlined"
            size="small"
            label={(() => {
              // Match the label to the operator's mental model per order type:
              // fuel = Carga / Descarga, delivery = Recolha / Entrega. Assistance
              // doesn't need the extra hint (the section title already says it).
              const base = lang === 'pt' ? 'Nome do local' : 'Location name';
              if (form.serviceType === 'fuel') {
                if (key === 'pickup')   return lang === 'pt' ? `${base} / Carga` : `${base} / Loading`;
                if (key === 'delivery') return lang === 'pt' ? `${base} / Descarga` : `${base} / Unloading`;
              }
              if (form.serviceType === 'delivery') {
                if (key === 'pickup')   return lang === 'pt' ? `${base} / Recolha` : `${base} / Pickup`;
                if (key === 'delivery') return lang === 'pt' ? `${base} / Entrega` : `${base} / Delivery`;
              }
              if (form.serviceType === 'assistance' && key === 'delivery') {
                return lang === 'pt' ? `${base} / Destino` : `${base} / Destination`;
              }
              return base;
            })()}
            value={section.locationName}
            onChange={(e) => updateField(key, 'locationName', e.target.value)}
          />
        </Box>
        <Box className={classes.row}>
          <AddressField
            label={lang === 'pt' ? 'Morada' : 'Address'}
            value={section.address}
            onChange={(val) => updateField(key, 'address', val)}
            onSelect={(item) => {
              updateField(key, 'address', item.name);
              updateField(key, 'coordinates', item.coordinates);
            }}
            classes={classes}
            lang={lang}
            country={clientSettings?.defaultCountry || 'PT'}
          />
        </Box>
        <Box className={classes.row}>
          <TextField
            className={classes.field}
            fullWidth
            variant="outlined"
            size="small"
            label={lang === 'pt' ? 'Contacto' : 'Contact'}
            value={section.contact}
            onChange={(e) => updateField(key, 'contact', e.target.value)}
            style={{ flex: 1 }}
          />
          <TextField
            className={classes.field}
            fullWidth
            variant="outlined"
            size="small"
            label={t(lang, 'phone') || (lang === 'pt' ? 'Telefone' : 'Phone')}
            value={section.phone}
            onChange={(e) => updateField(key, 'phone', e.target.value)}
            style={{ flex: 1 }}
          />
        </Box>
        {/* Email — só na secção de entrega; usado pela app para enviar
            o relatório/recibo ao cliente quando o serviço termina. */}
        {key === 'delivery' && (
          <Box style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <TextField
              className={classes.field}
              fullWidth
              variant="outlined"
              size="small"
              type="email"
              label={lang === 'pt' ? 'Email do cliente (para envio do relatório)' : 'Customer email (report delivery)'}
              value={section.email || ''}
              onChange={(e) => updateField(key, 'email', e.target.value)}
              style={{ flex: 1 }}
              inputProps={{ autoCapitalize: 'none', autoCorrect: 'off' }}
            />
          </Box>
        )}
        {/* Time window — modern date/time pickers */}
        <Typography style={{ fontSize: '0.58rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 4, marginBottom: 4 }}>
          {lang === 'pt' ? 'Janela horária' : 'Time window'}
        </Typography>
        <Box style={{ display: 'flex', gap: 6 }}>
          <Box style={{ flex: 1, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', padding: '6px 10px' }}>
            <Typography style={{ fontSize: '0.5rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 3 }}>
              {lang === 'pt' ? 'De' : 'From'}
            </Typography>
            <Box style={{ display: 'flex', gap: 4 }}>
              <input
                type="date"
                value={section.windowStart ? section.windowStart.split('T')[0] : ''}
                onChange={(e) => {
                  const time = section.windowStart ? section.windowStart.split('T')[1] || '08:00' : '08:00';
                  updateField(key, 'windowStart', e.target.value ? `${e.target.value}T${time}` : '');
                }}
                style={{ flex: 1, border: 'none', background: '#fff', borderRadius: 6, padding: '5px 8px', fontSize: '0.72rem', color: '#1e293b', outline: 'none', fontFamily: 'inherit' }}
              />
              <input
                type="time"
                value={section.windowStart ? (section.windowStart.split('T')[1] || '').slice(0, 5) : ''}
                onChange={(e) => {
                  const date = section.windowStart ? section.windowStart.split('T')[0] : new Date().toISOString().split('T')[0];
                  updateField(key, 'windowStart', e.target.value ? `${date}T${e.target.value}` : '');
                }}
                style={{ width: 80, border: 'none', background: '#fff', borderRadius: 6, padding: '5px 8px', fontSize: '0.72rem', color: '#1e293b', outline: 'none', fontFamily: 'inherit' }}
              />
            </Box>
          </Box>
          <Box style={{ flex: 1, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', padding: '6px 10px' }}>
            <Typography style={{ fontSize: '0.5rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 3 }}>
              {lang === 'pt' ? 'Até' : 'To'}
            </Typography>
            <Box style={{ display: 'flex', gap: 4 }}>
              <input
                type="date"
                value={section.windowEnd ? section.windowEnd.split('T')[0] : ''}
                onChange={(e) => {
                  const time = section.windowEnd ? section.windowEnd.split('T')[1] || '18:00' : '18:00';
                  updateField(key, 'windowEnd', e.target.value ? `${e.target.value}T${time}` : '');
                }}
                style={{ flex: 1, border: 'none', background: '#fff', borderRadius: 6, padding: '5px 8px', fontSize: '0.72rem', color: '#1e293b', outline: 'none', fontFamily: 'inherit' }}
              />
              <input
                type="time"
                value={section.windowEnd ? (section.windowEnd.split('T')[1] || '').slice(0, 5) : ''}
                onChange={(e) => {
                  const date = section.windowEnd ? section.windowEnd.split('T')[0] : new Date().toISOString().split('T')[0];
                  updateField(key, 'windowEnd', e.target.value ? `${date}T${e.target.value}` : '');
                }}
                style={{ width: 80, border: 'none', background: '#fff', borderRadius: 6, padding: '5px 8px', fontSize: '0.72rem', color: '#1e293b', outline: 'none', fontFamily: 'inherit' }}
              />
            </Box>
          </Box>
        </Box>
        <TextField
          className={classes.field}
          fullWidth
          variant="outlined"
          size="small"
          label={lang === 'pt' ? 'Instruções' : 'Instructions'}
          value={section.instructions}
          onChange={(e) => updateField(key, 'instructions', e.target.value)}
          multiline
          rows={2}
        />
      </CollapsibleSection>
    );
  }

  return (<>
    <Menu
      anchorEl={favoriteAnchor?.el}
      open={Boolean(favoriteAnchor)}
      onClose={() => { setFavoriteAnchor(null); setFavSearch(''); }}
      getContentAnchorEl={null}
      autoFocus={false}
      // Não roubar o foco para os itens — senão a escrita na pesquisa não entra.
      MenuListProps={{ autoFocus: false, autoFocusItem: false, style: { paddingTop: 0 } }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        style: {
          marginTop: 4, borderRadius: 10, minWidth: 340, maxHeight: 420,
          boxShadow: '0 12px 32px rgba(15,23,42,0.15)', padding: 4,
        },
      }}
      // Ensure menu renders above the OrderForm Dialog (z-index 9999)
      style={{ zIndex: 10100 }}
    >
      <Box style={{ padding: '6px 10px 8px', borderBottom: '1px solid #f1f5f9', marginBottom: 4 }}>
        <Typography style={{ fontSize: '0.58rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {lang === 'pt' ? 'Escolhe um favorito' : 'Pick a favorite'}
        </Typography>
        <Typography style={{ fontSize: '0.62rem', color: '#94a3b8', marginTop: 1 }}>
          {lang === 'pt' ? 'Nome, morada e coordenadas são preenchidos automaticamente' : 'Name, address and coordinates are auto-filled'}
        </Typography>
      </Box>

      {/* Pesquisa — filtra por nome, morada, categoria ou código de obra. */}
      <Box
        onKeyDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          margin: '0 6px 6px', padding: '0 10px', height: 38,
          background: '#f8fafc', border: '1px solid #e8edf3', borderRadius: 9,
        }}
      >
        <SearchIcon style={{ fontSize: 17, color: '#94a3b8', flexShrink: 0 }} />
        <InputBase
          autoFocus
          value={favSearch}
          onChange={e => setFavSearch(e.target.value)}
          placeholder={lang === 'pt' ? 'Procurar favorito…' : 'Search favorite…'}
          style={{ flex: 1, fontSize: '0.78rem', color: '#0f172a' }}
        />
        {favSearch && (
          <IconButton size="small" onClick={() => setFavSearch('')} style={{ padding: 2 }}>
            <CloseIcon style={{ fontSize: 14, color: '#94a3b8' }} />
          </IconButton>
        )}
      </Box>

      {(() => {
        const q = favSearch.trim().toLowerCase();
        const list = locations
          .slice()
          .filter(loc => {
            if (!q) return true;
            return [loc.name, loc.address, loc.category?.name, loc.obra?.codigoObra, loc.obra?.name]
              .some(v => v && String(v).toLowerCase().includes(q));
          })
          .sort((a, b) => (b.isPreferred ? 1 : 0) - (a.isPreferred ? 1 : 0) || (a.name || '').localeCompare(b.name || ''));

        if (locations.length === 0) {
          return (
            <MenuItem disabled style={{ fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic' }}>
              {lang === 'pt' ? 'Sem favoritos guardados' : 'No favorites saved'}
            </MenuItem>
          );
        }
        if (list.length === 0) {
          return (
            <MenuItem disabled style={{ fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic' }}>
              {lang === 'pt' ? 'Nenhum favorito corresponde à pesquisa' : 'No favorite matches'}
            </MenuItem>
          );
        }
        return list.map(loc => (
          <MenuItem
            key={loc.id}
            onClick={() => favoriteAnchor && pickFavorite(favoriteAnchor.sectionKey, loc)}
            style={{ borderRadius: 8, padding: '8px 10px', gap: 10, alignItems: 'flex-start' }}
          >
            <Box style={{
              width: 28, height: 28, borderRadius: 8,
              background: loc.category ? `${loc.category.color}1f` : '#f1f5f9',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              {loc.isPreferred
                ? <StarIcon style={{ fontSize: 14, color: '#eab308' }} />
                : <span style={{ fontSize: 13 }}>📍</span>}
            </Box>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Typography style={{ fontSize: '0.76rem', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {loc.name || (lang === 'pt' ? 'Sem nome' : 'Unnamed')}
              </Typography>
              {loc.address && (
                <Typography style={{ fontSize: '0.6rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {loc.address}
                </Typography>
              )}
              {/* Chips: categoria (cor do cliente) + código de obra (se ligado). */}
              {(loc.category || loc.obra || (loc.type && loc.type !== 'poi')) && (
                <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 3, alignItems: 'center' }}>
                  {loc.category && (
                    <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 17, padding: '0 7px', borderRadius: 999, background: `${loc.category.color}14`, border: `1px solid ${loc.category.color}40` }}>
                      <Box style={{ width: 6, height: 6, borderRadius: '50%', background: loc.category.color }} />
                      <Typography style={{ fontSize: '0.54rem', fontWeight: 700, color: loc.category.color, whiteSpace: 'nowrap' }}>{loc.category.name}</Typography>
                    </Box>
                  )}
                  {loc.obra && (
                    <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 3, height: 17, padding: '0 7px', borderRadius: 999, background: '#fef3c7', border: '1px solid #fcd34d' }}>
                      <HomeWorkIcon style={{ fontSize: 10, color: '#92400e' }} />
                      <Typography style={{ fontSize: '0.54rem', fontWeight: 700, color: '#92400e', whiteSpace: 'nowrap' }}>{loc.obra.codigoObra}</Typography>
                    </Box>
                  )}
                  {!loc.category && loc.type && loc.type !== 'poi' && (
                    <Typography style={{ fontSize: '0.54rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.04em' }}>
                      {loc.type}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </MenuItem>
        ));
      })()}
    </Menu>

    {/* Save-as-favorite overlay — custom full-screen modal using portals, sitting above the
        parent Dialog (z-index 9999). Avoids MUI's Dialog stacking quirks. */}
    {saveFavDialog && ReactDOM.createPortal(
      <Box
        onClick={() => !saveFavDialog.saving && setSaveFavDialog(null)}
        style={{
          // No backdrop-filter — some browsers leave the blur lingering on the parent paint
          // layer even after the overlay unmounts, making the OT form look disabled afterwards.
          position: 'fixed', inset: 0, zIndex: 12000,
          background: 'rgba(15,23,42,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Box
          onClick={e => e.stopPropagation()}
          style={{
            width: 'min(480px, calc(100% - 32px))',
            maxHeight: 'calc(100vh - 40px)',
            background: '#fff', borderRadius: 14,
            boxShadow: '0 20px 60px rgba(15,23,42,0.25)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}
        >
          <Box style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0',
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
          }}>
            <StarIcon style={{ fontSize: 20, color: '#eab308' }} />
            <Typography style={{ fontSize: '0.95rem', fontWeight: 700, color: '#92400e', flex: 1 }}>
              {lang === 'pt' ? 'Guardar nos favoritos' : 'Save as favorite'}
            </Typography>
            <IconButton size="small" onClick={() => setSaveFavDialog(null)} disabled={saveFavDialog.saving}>
              <CloseIcon style={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <Box style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto' }}>
            <TextField
              label={lang === 'pt' ? 'Nome' : 'Name'}
              value={saveFavDialog.name}
              onChange={e => setSaveFavDialog(s => ({ ...s, name: e.target.value }))}
              size="small" variant="outlined" fullWidth autoFocus
            />
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>{lang === 'pt' ? 'Tipo' : 'Type'}</InputLabel>
              <Select
                value={saveFavDialog.type}
                onChange={e => setSaveFavDialog(s => ({ ...s, type: e.target.value }))}
                label={lang === 'pt' ? 'Tipo' : 'Type'}
                MenuProps={{ style: { zIndex: 12100 } }}
              >
                <MenuItem value="warehouse">🏭 {lang === 'pt' ? 'Armazém' : 'Warehouse'}</MenuItem>
                <MenuItem value="safe_point">🛡️ {lang === 'pt' ? 'Ponto seguro' : 'Safe point'}</MenuItem>
                <MenuItem value="overnight">🛏️ {lang === 'pt' ? 'Dormida' : 'Overnight'}</MenuItem>
                <MenuItem value="fuel">⛽ {lang === 'pt' ? 'Combustível' : 'Fuel'}</MenuItem>
                <MenuItem value="poi">📍 {lang === 'pt' ? 'Ponto de interesse' : 'POI'}</MenuItem>
                <MenuItem value="rest_area">🅿️ {lang === 'pt' ? 'Área de descanso' : 'Rest area'}</MenuItem>
                <MenuItem value="garage">🔧 {lang === 'pt' ? 'Garagem' : 'Garage'}</MenuItem>
                <MenuItem value="other">⭐ {lang === 'pt' ? 'Outro' : 'Other'}</MenuItem>
              </Select>
            </FormControl>
            <Box style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <TextField
                label={lang === 'pt' ? 'Morada' : 'Address'}
                value={saveFavDialog.address}
                onChange={e => setSaveFavDialog(s => ({ ...s, address: e.target.value, coordinates: null }))}
                size="small" variant="outlined" fullWidth
              />
              <Button
                size="small"
                variant="outlined"
                onClick={geocodeSaveFavAddress}
                disabled={!saveFavDialog.address || saveFavDialog.geocoding}
                style={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', minWidth: 100, height: 40 }}
              >
                {saveFavDialog.geocoding
                  ? (lang === 'pt' ? 'A localizar…' : 'Locating…')
                  : (saveFavDialog.coordinates ? (lang === 'pt' ? '✓ Localizado' : '✓ Located') : (lang === 'pt' ? 'Localizar' : 'Locate'))}
              </Button>
            </Box>
            <TextField
              label={lang === 'pt' ? 'Notas' : 'Notes'}
              value={saveFavDialog.notes}
              onChange={e => setSaveFavDialog(s => ({ ...s, notes: e.target.value }))}
              size="small" variant="outlined" fullWidth multiline rows={2}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!saveFavDialog.isPreferred}
                  onChange={e => setSaveFavDialog(s => ({ ...s, isPreferred: e.target.checked }))}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography style={{ fontSize: '0.82rem' }}>
                {lang === 'pt' ? 'Marcar como preferencial' : 'Mark as preferred'}
              </Typography>}
            />
            {saveFavDialog.coordinates && (
              <Typography style={{ fontSize: '0.66rem', color: '#94a3b8' }}>
                📍 {Number(saveFavDialog.coordinates[1]).toFixed(5)}, {Number(saveFavDialog.coordinates[0]).toFixed(5)}
              </Typography>
            )}
          </Box>
          <Box style={{ padding: '10px 16px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#f8fafc' }}>
            <Button
              onClick={() => setSaveFavDialog(null)}
              disabled={saveFavDialog.saving}
              style={{ textTransform: 'none' }}
            >
              {lang === 'pt' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button
              onClick={commitSaveFavorite}
              disabled={saveFavDialog.saving || !saveFavDialog.name?.trim()}
              variant="contained" color="primary"
              style={{ textTransform: 'none', fontWeight: 700 }}
            >
              {saveFavDialog.saving
                ? (lang === 'pt' ? 'A guardar…' : 'Saving…')
                : (lang === 'pt' ? 'Guardar' : 'Save')}
            </Button>
          </Box>
        </Box>
      </Box>,
      document.body
    )}

    <ThemeProvider theme={dialogTheme}>
    <Dialog
      open={open}
      // Only close via explicit buttons — ignore backdrop clicks and escape so the operator
      // doesn't lose the form data by clicking outside while filling fields.
      onClose={(_e, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose(false);
      }}
      disableBackdropClick
      disableEscapeKeyDown
      className={classes.dialog} maxWidth="md"
      disablePortal={false}
      style={{ zIndex: 9999 }}
      BackdropProps={{ style: { background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)' } }}
      PaperProps={{ 'data-planner-theme': dark ? 'dark' : 'light' }}>
      <Box className={classes.title}>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Typography style={{ fontWeight: 800, fontSize: '1rem', color: '#1e293b' }}>
            {viewOnly
              ? (lang === 'pt' ? 'Consultar Ordem de Serviço' : 'View Service Order')
              : (isEdit ? t(lang, 'editOrder') : t(lang, 'createOrder'))}
          </Typography>
          {viewOnly && (
            <Box style={{
              padding: '3px 10px', borderRadius: 12,
              background: '#f1f5f9',
              border: '1px solid #e2e8f0',
              color: '#475569', fontSize: '0.62rem', fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="#475569"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
              {lang === 'pt' ? 'Modo Consulta' : 'View Mode'}
            </Box>
          )}
        </Box>
        <IconButton size="small" onClick={() => onClose(false)}>
          <CloseIcon style={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Box className={classes.content}>
        {/* In view-only mode: inner wrapper disables input but keeps the outer container scrollable */}
        <Box style={viewOnly ? { pointerEvents: 'none', opacity: 0.92 } : undefined}>
        {/* Custom reference — free-form text the operator types (customer
            code, project name, internal label). Surfaced wherever the OT is
            displayed alongside the auto-generated `OT-YYYY-NNNN`. Optional. */}
        <TextField
          className={classes.field}
          variant="outlined"
          size="small"
          fullWidth
          label={lang === 'pt' ? 'Nome / Referência da ordem' : 'Order name / reference'}
          placeholder={lang === 'pt' ? 'Ex: Cliente XYZ · Projeto ABC' : 'e.g. Client XYZ · Project ABC'}
          value={form.customRef}
          onChange={(e) => updateField(null, 'customRef', e.target.value)}
          inputProps={{ maxLength: 80 }}
          style={{ marginBottom: 10 }}
          InputProps={{ style: { fontSize: '0.85rem', fontWeight: 600 } }}
        />
        {/* Order type: delivery (cargo) vs assistance (technical service visit).
            Drives labels and routing semantics (Petrotec workflow). */}
        <Box style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 12px', marginBottom: 10,
          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
        }}>
          <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', flexShrink: 0 }}>
            {lang === 'pt' ? 'Tipo de ordem:' : 'Order type:'}
          </Typography>
          <Box style={{ display: 'flex', gap: 4, flex: 1 }}>
            {[
              // Soft pastel palette: accent only visible via a thin coloured border
              // and a subtle tinted background when active. No bold fill — easier on
              // the eyes when the operator has the form open for a long time.
              { value: 'delivery',   labelPt: '📦 Entrega',      labelEn: '📦 Delivery',   color: '#475569', bgActive: '#eef2ff', borderActive: '#c7d2fe' },
              { value: 'assistance', labelPt: '🔧 Assistência',  labelEn: '🔧 Assistance', color: '#475569', bgActive: '#f0fdf4', borderActive: '#bbf7d0' },
              { value: 'fuel',       labelPt: '⛽ Combustível',   labelEn: '⛽ Fuel',       color: '#475569', bgActive: '#fff7ed', borderActive: '#fed7aa' },
            ]
              .filter(opt => allowedTypes.includes(opt.value))
              .map(opt => {
              const active = form.serviceType === opt.value;
              return (
                <Box key={opt.value}
                  onClick={() => updateField(null, 'serviceType', opt.value)}
                  style={{
                    flex: 1, textAlign: 'center', padding: '6px 10px',
                    borderRadius: 6, cursor: 'pointer',
                    fontSize: '0.74rem', fontWeight: active ? 700 : 500,
                    background: active ? opt.bgActive : '#fff',
                    color: active ? '#0f172a' : '#64748b',
                    border: `1px solid ${active ? opt.borderActive : '#e2e8f0'}`,
                    transition: 'all 0.15s',
                  }}>
                  {lang === 'pt' ? opt.labelPt : opt.labelEn}
                </Box>
              );
            })}
          </Box>
          {/* Estimated on-site service time (Petrotec: how long the technician spends
              at the service location). Reuses the `unloadingTime` column. */}
          <Box style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <Typography style={{ fontSize: '0.66rem', color: '#64748b', fontWeight: 600 }}>
              {lang === 'pt'
                ? (form.serviceType === 'assistance' ? 'Tempo serviço:' : 'Tempo descarga:')
                : (form.serviceType === 'assistance' ? 'Service time:' : 'Unload time:')}
            </Typography>
            <TextField
              className={classes.field}
              variant="outlined" size="small" type="number"
              value={form.unloadingTime}
              onChange={(e) => updateField(null, 'unloadingTime', e.target.value)}
              inputProps={{ min: 0, step: 5, style: { padding: '5px 8px', width: 50, textAlign: 'right' } }}
              placeholder="min"
            />
            <Typography style={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600 }}>min</Typography>
          </Box>
        </Box>

        {/* Service date — explicit planned-date field. Available for ALL
            order types (was assistance-only) so the operator can plan
            deliveries and fuel runs days in advance with the same
            calendar-friendly field. The hint below the input adapts to
            the chosen type. */}
        <Box style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 12px', marginBottom: 10,
          background: '#fafbfc', border: '1px solid #e2e8f0', borderRadius: 8,
        }}>
          <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', flexShrink: 0 }}>
            📅 {lang === 'pt' ? 'Data do serviço:' : 'Service date:'}
          </Typography>
          <TextField
            className={classes.field}
            variant="outlined" size="small" type="date"
            value={form.serviceDate}
            onChange={(e) => updateField(null, 'serviceDate', e.target.value)}
            inputProps={{ style: { padding: '5px 8px', fontSize: '0.72rem' } }}
            style={{ flex: 1, maxWidth: 180 }}
            InputLabelProps={{ shrink: true }}
          />
          <Typography style={{ fontSize: '0.62rem', color: '#94a3b8', flex: 1 }}>
            {lang === 'pt'
              ? (form.serviceType === 'assistance'
                  ? 'Dia em que o técnico deve chegar ao local'
                  : form.serviceType === 'fuel'
                    ? 'Dia previsto para abastecimento'
                    : 'Dia previsto para entrega')
              : (form.serviceType === 'assistance'
                  ? 'Day the technician must arrive on site'
                  : form.serviceType === 'fuel'
                    ? 'Planned fuel-run day'
                    : 'Planned delivery day')}
          </Typography>
        </Box>


        {/* Petrotec Angola — bloco opcional. Só aparece em modo AO E para OTs
            de assistência. Auto-popula a morada de entrega a partir do posto. */}
        {form.serviceType === 'assistance' && (
          <AOPetrotecOrderSection
            aoForm={form.aoPetrotec}
            orderId={isEdit ? (order._id || order.id) : null}
            onChange={(next) => updateField(null, 'aoPetrotec', next)}
            onSiteSelected={(siteInfo) => {
              // Quando o operador escolhe um posto, esmagar morada+coords+nome
              // do local de serviço. Isto evita o bug em que mudar de posto
              // deixava as coords do posto anterior — o operador esperaria
              // ver o novo posto no mapa. Se quiser personalizar a morada,
              // faz isso depois de escolher o posto.
              updateField(null, 'delivery', {
                ...(form.delivery || {}),
                locationName: siteInfo.locationName || '',
                address: siteInfo.address || '',
                coordinates: siteInfo.coordinates || null,
              });
            }}
            onSlaSuggested={(hours) => {
              if (!form.slaHours || form.slaHours === '') {
                updateField(null, 'slaHours', String(hours));
              }
            }}
            onSlaResolutionSuggested={(hours) => {
              // Sprint F — auto-popular slaResolutionHours quando o
              // contrato tem targetResolutionHours definido. Quando
              // hours é null, NÃO esmaga (mantém o que estava).
              if (hours == null) return;
              if (!form.slaResolutionHours || form.slaResolutionHours === '') {
                updateField(null, 'slaResolutionHours', String(hours));
              }
            }}
            lang={lang}
          />
        )}

        {/* Move Express — bloco fuel. Mostra para qualquer tenant com OT
            fuel. Recolhe produto, capacidade esperada e comboio. */}
        {form.serviceType === 'fuel' && (
          <FuelOrderSection
            fuelForm={form.fuel}
            onChange={(next) => updateField(null, 'fuel', next)}
            lang={lang}
          />
        )}

        {/* Required skills — temporarily disabled. Re-enable when drivers have
            skills populated in the database. The schema and API already support it. */}
        {false && form.serviceType === 'assistance' && (
          <Box style={{
            padding: '10px 12px', marginBottom: 10,
            background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
          }}>
            <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', marginBottom: 6 }}>
              🎯 {lang === 'pt' ? 'Competências necessárias' : 'Required skills'}
            </Typography>
            <SkillsPicker
              value={form.requiredSkills}
              onChange={(next) => updateField(null, 'requiredSkills', next)}
              lang={lang}
              emptyLabel={lang === 'pt' ? 'Sem requisitos' : 'No requirements'}
              accent="#2563eb"
            />
          </Box>
        )}

        {/* Pickup + Delivery side-by-side — visual journey metaphor:
            esquerda = partida (recolha/saída) → direita = chegada (entrega/serviço). */}
        {/* Assistance OTs: only the service location (= delivery) is shown.
            The departure point (driver home / favourite warehouse) is now
            picked in the assign-vehicle dialog, so the old "saída = casa do
            motorista" pickup card was redundant and caused confusion. */}
        {form.serviceType === 'assistance' ? (
          <Box style={{ marginBottom: 10 }}>
            {renderLocationSection(
              'delivery',
              lang === 'pt' ? '🔧 Local do serviço' : '🔧 Service location'
            )}
          </Box>
        ) : (
          <Box style={{
            display: 'grid',
            gridTemplateColumns: '1fr 28px 1fr',
            gap: 0,
            alignItems: 'stretch',
            marginBottom: 10,
          }}>
            <Box style={{ minWidth: 0 }}>
              {renderLocationSection('pickup', (() => {
                if (form.serviceType === 'fuel') {
                  // The OT pickup = fuel depot (where the truck loads). The truck yard
                  // itself becomes the *route* origin when the operator creates a route.
                  return lang === 'pt' ? '⛽ Depósito de combustível (recolha)' : '⛽ Fuel depot (pickup)';
                }
                return t(lang, 'pickup');
              })())}
            </Box>
            {/* Arrow separator — subtle visual cue that the flow moves left→right */}
            <Box style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative',
              pointerEvents: 'none',
            }}>
              <Box style={{
                width: 26, height: 26, borderRadius: '50%',
                background: '#fff',
                border: '1px solid #e2e8f0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#94a3b8',
                zIndex: 1,
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8.59 16.59 13.17 12 8.59 7.41 10 6l6 6-6 6z"/>
                </svg>
              </Box>
            </Box>
            <Box style={{ minWidth: 0 }}>
              {renderLocationSection('delivery', (() => {
                if (form.serviceType === 'fuel') {
                  return lang === 'pt' ? '📍 Local de descarga' : '📍 Discharge point';
                }
                return t(lang, 'delivery');
              })())}
            </Box>
          </Box>
        )}

        {/* Cargo — collapsed by default (operator opens it only when needed) */}
        <CollapsibleSection title={t(lang, 'cargo')} defaultOpen={false}>
          <TextField
            className={classes.field}
            fullWidth
            variant="outlined"
            size="small"
            label={lang === 'pt' ? 'Descrição' : 'Description'}
            value={form.cargo.description}
            onChange={(e) => updateField('cargo', 'description', e.target.value)}
            style={{ marginBottom: 8 }}
          />
          <Box className={classes.row}>
            <TextField
              className={classes.field}
              variant="outlined"
              size="small"
              label={t(lang, 'cargoWeight')}
              type="number"
              value={form.cargo.weight}
              onChange={(e) => updateField('cargo', 'weight', e.target.value)}
              style={{ flex: 1 }}
            />
            <TextField
              className={classes.field}
              variant="outlined"
              size="small"
              label={t(lang, 'cargoVolume')}
              type="number"
              value={form.cargo.volume}
              onChange={(e) => updateField('cargo', 'volume', e.target.value)}
              style={{ flex: 1 }}
            />
            <TextField
              className={classes.field}
              variant="outlined"
              size="small"
              label={t(lang, 'cargoPallets')}
              type="number"
              value={form.cargo.pallets}
              onChange={(e) => updateField('cargo', 'pallets', e.target.value)}
              style={{ flex: 1 }}
            />
          </Box>
          <FormControl variant="outlined" size="small" fullWidth className={classes.field}>
            <InputLabel>{t(lang, 'cargoType')}</InputLabel>
            <Select
              value={form.cargo.type}
              onChange={(e) => updateField('cargo', 'type', e.target.value)}
              label={t(lang, 'cargoType')}
            >
              <MenuItem value="Paletes">Paletes</MenuItem>
              <MenuItem value="FTL">FTL</MenuItem>
              <MenuItem value="Granel">Granel</MenuItem>
              <MenuItem value="Outro">{lang === 'pt' ? 'Outro' : 'Other'}</MenuItem>
            </Select>
          </FormControl>
        </CollapsibleSection>

        {/* Values — collapsed by default */}
        <CollapsibleSection title={lang === 'pt' ? 'Valores' : 'Values'} defaultOpen={isMercadoria}>
          <Box className={classes.row}>
            <TextField
              className={classes.field}
              variant="outlined"
              size="small"
              label={isMercadoria
                ? (lang === 'pt' ? 'Custo da entrega (\u20AC)' : 'Delivery cost (\u20AC)')
                : `${t(lang, 'agreedPrice')} (\u20AC)`}
              type="number"
              value={form.agreedPrice}
              onChange={(e) => updateField(null, 'agreedPrice', e.target.value)}
              helperText={isMercadoria
                ? (lang === 'pt' ? 'Custo desta entrega' : 'Cost of this delivery')
                : (lang === 'pt' ? 'Preço cobrado ao cliente' : 'Price charged to customer')}
              style={{ flex: 1 }}
            />
            <TextField
              className={classes.field}
              variant="outlined"
              size="small"
              label={lang === 'pt' ? 'Referência externa' : 'External reference'}
              value={form.externalRef}
              onChange={(e) => updateField(null, 'externalRef', e.target.value)}
              style={{ flex: 1 }}
            />
          </Box>
        </CollapsibleSection>

        {/* Documentos — anexar à OT (mercadoria/Casais). Novas OT: upload após gravar. */}
        {isMercadoria && (
        <CollapsibleSection title={lang === 'pt' ? 'Documentos' : 'Documents'} defaultOpen={false}>
          <input
            type="file"
            id="ot-doc-input"
            style={{ display: 'none' }}
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length) setPendingDocs(prev => [...prev, ...files]);
              e.target.value = '';
            }}
          />
          <Button variant="outlined" size="small" onClick={() => document.getElementById('ot-doc-input')?.click()}>
            {lang === 'pt' ? '+ Adicionar documento' : '+ Add document'}
          </Button>
          {pendingDocs.length > 0 && (
            <Box style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {pendingDocs.map((f, i) => (
                <Box key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.74rem', color: '#334155' }}>
                  <span>📎</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                  <span style={{ cursor: 'pointer', color: '#ef4444', fontWeight: 700 }} onClick={() => setPendingDocs(prev => prev.filter((_, j) => j !== i))}>✕</span>
                </Box>
              ))}
            </Box>
          )}
          <Typography style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 6 }}>
            {lang === 'pt' ? 'Os documentos são anexados à OT ao gravar.' : 'Documents are attached to the OT on save.'}
          </Typography>
        </CollapsibleSection>
        )}

        {/* Driver & Vehicle — removed: assignment is done via Dispatch, not on OT edit */}

        {/* Priority — chip buttons */}
        <CollapsibleSection title={t(lang, 'priority')}>
          <Box style={{ display: 'flex', gap: 8 }}>
            {[
              // Soft pastel: accent only visible when active via a faint tinted
              // background + coloured border. No saturated fill.
              { value: 'low',    label: lang === 'pt' ? 'Baixa' : 'Low',       dot: '#94a3b8', bgActive: '#f8fafc', borderActive: '#cbd5e1' },
              { value: 'normal', label: 'Normal',                              dot: '#64748b', bgActive: '#f1f5f9', borderActive: '#cbd5e1' },
              { value: 'high',   label: lang === 'pt' ? 'Urgente' : 'Urgent',  dot: '#ef4444', bgActive: '#fef2f2', borderActive: '#fecaca' },
            ].map(p => {
              const active = form.priority === p.value;
              return (
                <Box
                  key={p.value}
                  onClick={() => updateField(null, 'priority', p.value)}
                  style={{
                    flex: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                    fontSize: '0.74rem', fontWeight: active ? 700 : 500,
                    background: active ? p.bgActive : '#fff',
                    color: active ? '#0f172a' : '#64748b',
                    border: `1px solid ${active ? p.borderActive : '#e2e8f0'}`,
                    transition: 'all 0.15s',
                  }}
                >
                  <Box style={{ width: 6, height: 6, borderRadius: '50%', background: p.dot }} />
                  {p.label}
                </Box>
              );
            })}
          </Box>
        </CollapsibleSection>

        {/* Delivery-time / contractual deadline — optional. Drives route ordering. */}
        <CollapsibleSection title={lang === 'pt' ? 'Tempo de entrega' : 'Delivery time'}>
          <Typography style={{ fontSize: '0.66rem', color: '#94a3b8', marginBottom: 6 }}>
            {lang === 'pt'
              ? 'Tempo em horas para entrega/resposta a partir da criação da ordem. Quando definido, o cálculo da rota prioriza estas OTs antes do km/tempo.'
              : 'Delivery/response time in hours from creation. When set, the route builder prioritises these OTs before km/time.'}
          </Typography>
          <Box style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {SLA_PRESETS.map(p => {
              const active = Number(form.slaHours) === p.value;
              return (
                <Box
                  key={p.value}
                  onClick={() => updateField(null, 'slaHours', String(p.value))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
                    fontSize: '0.72rem', fontWeight: active ? 700 : 500,
                    background: active ? '#f8fafc' : '#fff',
                    color: active ? '#0f172a' : '#64748b',
                    border: `1px solid ${active ? '#cbd5e1' : '#e2e8f0'}`,
                    transition: 'all 0.15s',
                  }}
                >
                  <Box style={{ width: 6, height: 6, borderRadius: '50%', background: p.color }} />
                  {lang === 'pt' ? p.labelPt : p.labelEn}
                </Box>
              );
            })}
            <Box
              onClick={() => updateField(null, 'slaHours', '')}
              style={{
                padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
                fontSize: '0.72rem', fontWeight: form.slaHours === '' ? 700 : 500,
                background: form.slaHours === '' ? '#f8fafc' : '#fff',
                color: form.slaHours === '' ? '#0f172a' : '#64748b',
                border: `1px solid ${form.slaHours === '' ? '#cbd5e1' : '#e2e8f0'}`,
              }}
            >
              {lang === 'pt' ? 'Sem prazo' : 'No deadline'}
            </Box>
          </Box>
          <Box style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Typography style={{ fontSize: '0.7rem', color: '#64748b' }}>
              {lang === 'pt' ? 'Ou horas personalizadas:' : 'Or custom hours:'}
            </Typography>
            <TextField
              className={classes.field}
              variant="outlined" size="small" type="number"
              value={form.slaHours}
              onChange={(e) => updateField(null, 'slaHours', e.target.value)}
              inputProps={{ min: 0, step: 1, style: { padding: '6px 10px', width: 70 } }}
              placeholder="h"
            />
          </Box>
        </CollapsibleSection>

        {/* Notes */}
        <CollapsibleSection title={lang === 'pt' ? 'Notas' : 'Notes'} defaultOpen={false}>
          <TextField
            className={classes.field}
            fullWidth
            variant="outlined"
            size="small"
            label={lang === 'pt' ? 'Notas adicionais' : 'Additional notes'}
            value={form.notes}
            onChange={(e) => updateField(null, 'notes', e.target.value)}
            multiline
            rows={3}
          />
        </CollapsibleSection>
        </Box>
      </Box>

      {error && (
        <Box style={{ margin: '0 20px 8px', padding: '8px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
          <Typography style={{ fontSize: '0.72rem', color: '#dc2626', fontWeight: 600 }}>{error}</Typography>
        </Box>
      )}
      <Box className={classes.actions}>
        <Button className={classes.cancelBtn} onClick={() => onClose(false)}>
          {viewOnly ? (lang === 'pt' ? 'Fechar' : 'Close') : t(lang, 'cancel')}
        </Button>
        {viewOnly && (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PrintIcon style={{ fontSize: 16 }} />}
              onClick={() => generateOrderPdf(form, order, lang, 'print')}
              style={{ textTransform: 'none', fontWeight: 600, fontSize: '0.74rem' }}
            >
              {lang === 'pt' ? 'Imprimir' : 'Print'}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<PictureAsPdfIcon style={{ fontSize: 16 }} />}
              onClick={() => generateOrderPdf(form, order, lang, 'save')}
              style={{ textTransform: 'none', fontWeight: 600, fontSize: '0.74rem' }}
            >
              PDF
            </Button>
          </>
        )}
        {!viewOnly && (
          <Button className={classes.saveBtn} onClick={handleSubmit} disabled={saving}>
            {saving ? (lang === 'pt' ? 'A guardar...' : 'Saving...') : (isEdit ? t(lang, 'save') : t(lang, 'createOrder'))}
          </Button>
        )}
      </Box>
    </Dialog>
    </ThemeProvider>

    {/* Fase 2 — guarda morada↔rota. Ao alterar o local de serviço de uma
        OT que pertence a uma rota, o operador decide: gravar só a morada
        ou gravar e recalcular a rota. Portal para document.body para
        ficar acima do Dialog da OT. */}
    {routeGuard && ReactDOM.createPortal((() => {
      const pt = lang === 'pt';
      const r = routeGuard.routeName;
      const d = routeGuard.driverName || (pt ? 'o motorista' : 'the driver');
      const TONE = {
        info: '#3b82f6', warning: '#f59e0b', error: '#ef4444',
      };
      const kind = routeGuard.changeKind || 'location';
      const involvesWindow = kind === 'window' || kind === 'both';
      const whatChanged = pt
        ? (kind === 'both' ? 'o local de serviço e a janela horária' : kind === 'window' ? 'a janela horária' : 'o local de serviço')
        : (kind === 'both' ? 'the service location and the time window' : kind === 'window' ? 'the time window' : 'the service location');
      const A = {
        sync: { label: pt ? 'Guardar e actualizar a rota' : 'Save and update the route', onClick: commitAndSyncRoute },
        keep: { label: pt ? 'Guardar e recalcular (manter ordem)' : 'Save and recompute (keep order)', onClick: commitAndSyncRoute },
        reopt: { label: involvesWindow
          ? (pt ? 'Guardar e re-otimizar pela janela horária' : 'Save and re-optimize by time window')
          : (pt ? 'Guardar e re-optimizar a rota' : 'Save and re-optimize the route'), onClick: commitAndReoptimize },
        only: { label: involvesWindow
          ? (pt ? 'Guardar só a OT (não mexer na rota)' : 'Save OT only (leave route)')
          : (pt ? 'Guardar só a morada' : 'Save address only'), onClick: commitGuardedSave },
      };
      const CFG = {
        planned: {
          severity: 'info',
          title: pt ? 'OT pertence a uma rota' : 'OT belongs to a route',
          message: pt
            ? `Esta OT está na rota «${r}» (planeada). A rota foi calculada com a morada anterior.\n\nComo queres gravar a alteração do local de serviço?`
            : `This OT is on route “${r}” (planned), computed with the old address.\n\nHow do you want to save the service location change?`,
          actions: [{ ...A.sync, primary: true }, A.only],
        },
        assigned: {
          severity: 'warning',
          title: pt ? 'OT numa rota atribuída' : 'OT on an assigned route',
          message: pt
            ? `Esta OT está na rota «${r}», atribuída ao motorista ${d}.\n\nSe actualizares a rota, a geometria é recalculada com a nova morada — a ordem das paragens mantém-se.`
            : `This OT is on route “${r}”, assigned to ${d}.\n\nUpdating the route recomputes its geometry with the new address — the stop order is kept.`,
          actions: [{ ...A.sync, primary: true }, A.only],
        },
        in_transit: {
          severity: 'error',
          title: pt ? 'Rota em trânsito' : 'Route in transit',
          message: pt
            ? `A rota «${r}» está EM TRÂNSITO com o motorista ${d}.\n\nSe ele já estiver a caminho desta paragem vai ter de renavegar. As OT já entregues ficam fixas. Escolhe como actualizar:`
            : `Route “${r}” is IN TRANSIT with ${d}.\n\nIf the driver is already heading to this stop they will have to re-navigate. Delivered OTs stay fixed. Choose how to update:`,
          actions: [{ ...A.reopt, primary: true }, A.keep, A.only],
        },
      };
      // Mudança de JANELA HORÁRIA (ou local+janela): a rota foi ordenada com a
      // janela anterior — a ação relevante é re-otimizar por janela (a opção de
      // "manter ordem" não faria sentido aqui). As entregues ficam sempre fixas.
      const WIN_CFG = {
        planned: {
          severity: 'info',
          title: pt ? 'OT pertence a uma rota' : 'OT belongs to a route',
          message: pt
            ? `Esta OT está na rota «${r}» (planeada), ordenada pela janela horária anterior.\n\nAlteraste ${whatChanged}. Queres re-otimizar a rota pela janela horária?`
            : `This OT is on route “${r}” (planned), ordered by the previous time window.\n\nYou changed ${whatChanged}. Re-optimize the route by time window?`,
          actions: [{ ...A.reopt, primary: true }, A.only],
        },
        assigned: {
          severity: 'warning',
          title: pt ? 'OT numa rota atribuída' : 'OT on an assigned route',
          message: pt
            ? `Esta OT está na rota «${r}», atribuída a ${d}, ordenada pela janela anterior.\n\nAlteraste ${whatChanged}. Re-otimizar reordena as paragens pela janela horária e notifica o motorista.`
            : `This OT is on route “${r}”, assigned to ${d}, ordered by the previous window.\n\nYou changed ${whatChanged}. Re-optimizing reorders the stops by time window and notifies the driver.`,
          actions: [{ ...A.reopt, primary: true }, A.only],
        },
        in_transit: {
          severity: 'error',
          title: pt ? 'Rota em trânsito' : 'Route in transit',
          message: pt
            ? `A rota «${r}» está EM TRÂNSITO com ${d}.\n\nAlteraste ${whatChanged}. Re-otimizar reordena as paragens POR ENTREGAR pela janela horária (as entregues ficam fixas) e o motorista é notificado.`
            : `Route “${r}” is IN TRANSIT with ${d}.\n\nYou changed ${whatChanged}. Re-optimizing reorders the PENDING stops by time window (delivered ones stay fixed) and notifies the driver.`,
          actions: [{ ...A.reopt, primary: true }, A.only],
        },
      };
      const cfg = (involvesWindow ? WIN_CFG : CFG)[routeGuard.scenario] || (involvesWindow ? WIN_CFG : CFG).planned;
      const tone = TONE[cfg.severity];
      const btnBase = {
        textTransform: 'none', fontSize: '0.8rem', fontWeight: 700,
        borderRadius: 10, padding: '10px 16px', width: '100%',
      };
      return (
        <>
          <Box onClick={() => setRouteGuard(null)} style={{
            position: 'fixed', inset: 0, zIndex: 13000,
            background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)',
          }} />
          <Box style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
            zIndex: 13001, width: 440, maxWidth: '92vw',
            background: '#fff', borderRadius: 16, overflow: 'hidden',
            boxShadow: '0 24px 64px rgba(0,0,0,0.28)',
          }}>
            <Box style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '14px 18px', background: `${tone}14`,
              borderBottom: `1px solid ${tone}33`,
            }}>
              <Box style={{ width: 8, height: 8, borderRadius: 4, background: tone }} />
              <Typography style={{ flex: 1, fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>
                {cfg.title}
              </Typography>
              <IconButton size="small" onClick={() => setRouteGuard(null)}>
                <CloseIcon style={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            <Box style={{ padding: '16px 20px' }}>
              <Typography style={{
                fontSize: '0.82rem', color: '#475569', lineHeight: 1.55,
                whiteSpace: 'pre-line',
              }}>
                {cfg.message}
              </Typography>
            </Box>
            <Box style={{
              display: 'flex', flexDirection: 'column', gap: 8,
              padding: '4px 20px 18px',
            }}>
              {cfg.actions.map((a, i) => (
                <Button
                  key={i}
                  onClick={a.onClick}
                  style={{
                    ...btnBase,
                    background: a.primary ? tone : '#f1f5f9',
                    color: a.primary ? '#fff' : '#334155',
                  }}
                >
                  {a.label}
                </Button>
              ))}
              <Button
                onClick={() => setRouteGuard(null)}
                style={{ ...btnBase, fontWeight: 600, color: '#64748b', padding: '8px 16px' }}
              >
                {pt ? 'Cancelar' : 'Cancel'}
              </Button>
            </Box>
          </Box>
        </>
      );
    })(), document.body)}
  </>);
}

// PDF generation for a transport order (viewOnly mode).
// mode = 'print' opens a print dialog, 'save' triggers a download.
function generateOrderPdf(form, order, lang, mode) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const isPt = lang === 'pt';
  const margin = 15;
  let y = margin;
  const orderNumber = order?.orderNumber || order?.id || '—';

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(`${isPt ? 'Ordem de Transporte' : 'Transport Order'} #${orderNumber}`, margin, y);
  y += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text(new Date().toLocaleString('pt-PT'), margin, y);
  y += 8;
  doc.setTextColor(0);

  function section(title, rows) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setFillColor(241, 245, 249);
    doc.rect(margin, y - 4, 180, 6, 'F');
    doc.text(title, margin + 2, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    rows.forEach(([k, v]) => {
      if (v == null || v === '') return;
      const text = `${k}: ${String(v)}`;
      const split = doc.splitTextToSize(text, 180);
      split.forEach(line => {
        if (y > 275) { doc.addPage(); y = margin; }
        doc.text(line, margin + 2, y);
        y += 4.5;
      });
    });
    y += 3;
  }

  const pickup = form.pickup || {};
  const delivery = form.delivery || {};
  section(isPt ? 'Recolha' : 'Pickup', [
    [isPt ? 'Morada' : 'Address', pickup.address],
    [isPt ? 'Contacto' : 'Contact', pickup.contactName],
    [isPt ? 'Telefone' : 'Phone', pickup.phone],
    [isPt ? 'Janela' : 'Window', [pickup.windowFrom, pickup.windowTo].filter(Boolean).join(' – ')],
    [isPt ? 'Notas' : 'Notes', pickup.notes],
  ]);
  section(isPt ? 'Entrega' : 'Delivery', [
    [isPt ? 'Morada' : 'Address', delivery.address],
    [isPt ? 'Contacto' : 'Contact', delivery.contactName],
    [isPt ? 'Telefone' : 'Phone', delivery.phone],
    [isPt ? 'Janela' : 'Window', [delivery.windowFrom, delivery.windowTo].filter(Boolean).join(' – ')],
    [isPt ? 'Notas' : 'Notes', delivery.notes],
  ]);
  section(isPt ? 'Carga' : 'Cargo', [
    [isPt ? 'Descrição' : 'Description', form.cargoDescription],
    [isPt ? 'Peso' : 'Weight', form.weight],
    [isPt ? 'Volume' : 'Volume', form.volume],
    [isPt ? 'Paletes' : 'Pallets', form.pallets],
  ]);
  section(isPt ? 'Atribuição' : 'Assignment', [
    [isPt ? 'Motorista' : 'Driver', order?.driverName],
    [isPt ? 'Viatura' : 'Vehicle', order?.vehiclePlate],
    [isPt ? 'Estado' : 'Status', order?.status],
    [isPt ? 'Entregue em' : 'Delivered at', order?.deliveredAt],
  ]);
  if (form.notes) section(isPt ? 'Notas' : 'Notes', [[isPt ? 'Texto' : 'Text', form.notes]]);

  if (mode === 'print') {
    doc.autoPrint();
    window.open(doc.output('bloburl'), '_blank');
  } else {
    doc.save(`OT-${orderNumber}.pdf`);
  }
}

export default OrderForm;
